import { type SetStateAction, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buildSummaryDetailsCopy } from '@/components/shared/link-preview/summary/details';
import { buildSummaryDisplay } from '@/components/shared/link-preview/summary/display';
import { streamLinkSummary } from '@/components/shared/link-preview/summary/generators';
import type { SummaryDisplay } from '@/components/shared/link-preview/summary/types';
import { isAbortError } from '@/components/shared/link-preview/summary/utils';
import type { ModelOption } from '@/lib/link-preview/summary-options';
import { canonicalizeSummaryModel, DEFAULT_SUMMARY_LENGTH_INDEX, getSummaryLengthOptionByIndex, SUMMARY_LENGTH_OPTIONS } from '@/lib/link-preview/summary-options';
import { DEFAULT_SUMMARY_METADATA, type SummaryState } from './types';
export interface LinkShareContextEntry {
  author: string;
  handle?: string | null;
  likeCount?: number;
  replyCount?: number;
  reshareCount?: number;
  text: string;
  timestamp?: string | null;
  viewCount?: number;
}
interface UseLinkSummaryArguments {
  powerUserEnabled?: boolean;
  shareContext?: LinkShareContextEntry[];
  url: string;
}
interface UseLinkSummaryReturn {
  canonicalDraftModel: string;
  dismissSummary: () => void;
  draftModel: ModelOption;
  draftSummaryLengthIndex: number;
  handleSettingsSummarize: (event: React.FormEvent<HTMLFormElement>) => void;
  handleSummarizeButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isSummarizing: boolean;
  isSummaryDismissed: boolean;
  selectedModel: ModelOption;
  setDraftModel: (model: ModelOption) => void;
  setDraftSummaryLengthIndex: (index: number) => void;
  setSettingsOpen: (next: boolean) => void;
  settingsOpen: boolean;
  shouldShowSummarizeControls: boolean;
  summaryDetailsCopy: string;
  summaryDisplay: SummaryDisplay;
  summaryLengthIndex: number;
  summaryState: SummaryState;
  triggerSummarization: (options?: {
    lengthIndex?: number;
    model?: ModelOption;
  }) => Promise<void>;
}
export function useLinkSummary({
  url,
  powerUserEnabled = false,
  shareContext
}: UseLinkSummaryArguments): UseLinkSummaryReturn {
  const summarizationAbortReference = useRef<AbortController | null>(null);
  const isMountedReference = useRef(false);
  const lastResetUrlReference = useRef<string | null>(null);
  const [summaryState, setSummaryState] = useState<SummaryState>(() => ({
    status: 'idle',
    text: '',
    error: null,
    metadata: {
      ...DEFAULT_SUMMARY_METADATA
    }
  }));
  const [summaryLengthIndex, setSummaryLengthIndex] = useState<number>(DEFAULT_SUMMARY_LENGTH_INDEX);
  const [selectedModel, setSelectedModel] = useState<ModelOption>('model.reasoning.fast');
  const [settingsOpenState, setSettingsOpenState] = useState(false);
  const [draftSummaryLengthIndex, setDraftSummaryLengthIndex] = useState(summaryLengthIndex);
  const [draftModel, setDraftModel] = useState<ModelOption>(selectedModel);
  const [isSummaryDismissed, setIsSummaryDismissed] = useState(false);
  const resetSummaryState = useCallback(() => {
    setSummaryState({
      status: 'idle',
      text: '',
      error: null,
      metadata: {
        ...DEFAULT_SUMMARY_METADATA
      }
    });
    setIsSummaryDismissed(false);
  }, []);
  const syncDraftSettings = useCallback(() => {
    setDraftSummaryLengthIndex(summaryLengthIndex);
    setDraftModel(selectedModel);
  }, [selectedModel, summaryLengthIndex]);
  const setSettingsOpen = useCallback((next: SetStateAction<boolean>) => {
    setSettingsOpenState(previous => {
      const resolved = typeof next === 'function' ? (next as (value: boolean) => boolean)(previous) : next;
      if (resolved && !previous) {
        syncDraftSettings();
      }
      return resolved;
    });
  }, [syncDraftSettings]);
  const settingsOpen = settingsOpenState;
  useLayoutEffect(() => {
    isMountedReference.current = true;
    return () => {
      isMountedReference.current = false;
      if (summarizationAbortReference.current) {
        summarizationAbortReference.current.abort();
        summarizationAbortReference.current = null;
      }
    };
  }, []);
  useLayoutEffect(() => {
    if (summarizationAbortReference.current) {
      summarizationAbortReference.current.abort();
      summarizationAbortReference.current = null;
    }
    resetSummaryState();
    lastResetUrlReference.current = url;
  }, [resetSummaryState, url]);
  const isSummarizing = summaryState.status === 'loading' || summaryState.status === 'streaming';

  // Core summarization routine; guards against pre-mount triggers so React never schedules state updates too early.
  const runSummarization = useCallback(async ({
    lengthIndex,
    model
  }: {
    lengthIndex: number;
    model: ModelOption;
  }) => {
    if (isSummarizing) {
      return;
    }
    const fallbackOption = getSummaryLengthOptionByIndex(DEFAULT_SUMMARY_LENGTH_INDEX) ?? SUMMARY_LENGTH_OPTIONS[0];
    const option = getSummaryLengthOptionByIndex(lengthIndex) ?? fallbackOption;
    const resolvedIndex = SUMMARY_LENGTH_OPTIONS.findIndex(candidate => candidate.value === option.value);
    const effectiveIndex = resolvedIndex === -1 ? DEFAULT_SUMMARY_LENGTH_INDEX : resolvedIndex;
    const effectiveLengthValue = option.value;
    const controller = new AbortController();
    if (summarizationAbortReference.current) {
      summarizationAbortReference.current.abort();
    }
    summarizationAbortReference.current = controller;
    if (!isMountedReference.current) {
      // If the hook unmounted before we could schedule, abort to avoid React warnings.
      summarizationAbortReference.current = null;
      return;
    }
    setSummaryLengthIndex(effectiveIndex);
    setSelectedModel(model);
    setDraftSummaryLengthIndex(effectiveIndex);
    setDraftModel(model);
    setIsSummaryDismissed(false);
    if (isMountedReference.current) {
      setSummaryState({
        status: 'loading',
        text: '',
        error: null,
        metadata: {
          ...DEFAULT_SUMMARY_METADATA,
          summaryLength: effectiveLengthValue
        }
      });
    }
    try {
      await streamLinkSummary({
        url,
        controller,
        setSummaryState,
        isMountedRef: isMountedReference,
        summaryLength: effectiveLengthValue,
        model,
        shareContext: shareContext ?? []
      });
    } catch (caughtError) {
      if (isAbortError(caughtError) || controller.signal.aborted) {
        return;
      }
      const message = caughtError instanceof Error ? caughtError.message : 'Unable to summarize link.';
      if (isMountedReference.current) {
        setSummaryState(previous_0 => ({
          status: 'error',
          text: previous_0.text,
          error: message,
          metadata: {
            ...previous_0.metadata
          }
        }));
      }
    } finally {
      summarizationAbortReference.current = null;
    }
  }, [isSummarizing, shareContext, url]);
  const handleSummarize = useCallback(() => {
    if (isSummarizing) {
      return;
    }
    runSummarization({
      lengthIndex: summaryLengthIndex,
      model: selectedModel
    }).catch((error: unknown) => {
      console.error('Failed to summarize link', error);
    });
  }, [isSummarizing, runSummarization, summaryLengthIndex, selectedModel]);
  const handleSummarizeButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleSummarize();
  }, [handleSummarize]);
  const handleSettingsSummarize = useCallback((event_0: React.FormEvent<HTMLFormElement>) => {
    event_0.preventDefault();
    if (isSummarizing) {
      return;
    }
    setSettingsOpen(false);
    runSummarization({
      lengthIndex: draftSummaryLengthIndex,
      model: draftModel
    }).catch((error_0: unknown) => {
      console.error('Failed to summarize link from settings', error_0);
    });
  }, [draftModel, draftSummaryLengthIndex, isSummarizing, runSummarization, setSettingsOpen]);
  const triggerSummarization = useCallback(async (options?: {
    lengthIndex?: number;
    model?: ModelOption;
  }) => {
    const resolvedLengthIndex = options?.lengthIndex ?? summaryLengthIndex;
    const resolvedModel = options?.model ?? selectedModel;
    await runSummarization({
      lengthIndex: resolvedLengthIndex,
      model: resolvedModel
    });
  }, [runSummarization, selectedModel, summaryLengthIndex]);
  const summaryDisplay = useMemo(() => buildSummaryDisplay({
    summaryState,
    summaryLengthIndex,
    selectedModel,
    isSummarizing
  }), [isSummarizing, selectedModel, summaryLengthIndex, summaryState]);
  const dismissSummary = useCallback(() => {
    setIsSummaryDismissed(true);
  }, []);
  const shouldShowSummarizeControls = !summaryDisplay.summaryHasContent || summaryState.status === 'error' || isSummaryDismissed;
  const summaryDetailsCopy = useMemo(() => buildSummaryDetailsCopy({
    resolvedModelLabel: summaryDisplay.resolvedModelLabel,
    resolvedSummaryLengthLabel: summaryDisplay.resolvedSummaryLengthLabel,
    modelId: summaryState.metadata.model,
    summaryTruncated: summaryDisplay.summaryTruncated,
    contentTruncated: summaryDisplay.contentTruncated,
    sourceCharacters: summaryState.metadata.sourceCharacters,
    contentBudgetCharacters: summaryState.metadata.contentBudgetCharacters,
    transcriptAvailable: summaryState.metadata.transcriptAvailable,
    transcriptSource: summaryState.metadata.transcriptSource,
    usage: summaryState.metadata.usage,
    cost: summaryState.metadata.cost,
    powerUserEnabled
  }), [summaryDisplay, summaryState.metadata.model, summaryState.metadata.contentBudgetCharacters, summaryState.metadata.sourceCharacters, summaryState.metadata.transcriptAvailable, summaryState.metadata.transcriptSource, summaryState.metadata.usage, summaryState.metadata.cost, powerUserEnabled]);
  const canonicalDraftModel = canonicalizeSummaryModel(draftModel);
  return {
    summaryState,
    summaryDisplay,
    isSummarizing,
    settingsOpen,
    setSettingsOpen,
    handleSummarizeButtonClick,
    handleSettingsSummarize,
    triggerSummarization,
    summaryLengthIndex,
    draftSummaryLengthIndex,
    setDraftSummaryLengthIndex: (index: number) => {
      setDraftSummaryLengthIndex(index);
    },
    selectedModel,
    draftModel,
    setDraftModel,
    canonicalDraftModel,
    summaryDetailsCopy,
    shouldShowSummarizeControls,
    isSummaryDismissed,
    dismissSummary
  };
}